/**
 * UI Components for The Clip Looper
 */

import { el, createElement, setText, setVisible, show, hide } from './dom.js';
import { formatFileSize, formatResolution, formatTime, formatPercent, truncateFilename } from '../utils/format.js';
import { formatDurationEstimate, formatSecondsDecimal } from '../utils/duration.js';
import {
  MIN_REPEAT_COUNT,
  MAX_REPEAT_COUNT,
  MIN_CROSSFADE_DURATION,
  MAX_CROSSFADE_DURATION
} from '../utils/validation.js';

/**
 * Create the loading indicator component
 *
 * @returns {HTMLElement}
 */
export function createLoadingIndicator() {
  return el.div({ className: 'loading-indicator', id: 'loadingIndicator' }, [
    el.div({ className: 'loading-spinner' }),
    el.p({ className: 'loading-text', id: 'loadingText' }, ['Loading video engine...'])
  ]);
}

/**
 * Create the upload zone component
 *
 * @param {object} handlers - Event handlers
 * @param {function} handlers.onFileSelect - Called when file is selected
 * @returns {HTMLElement}
 */
export function createUploadZone(handlers) {
  const fileInput = el.input({
    type: 'file',
    id: 'fileInput',
    accept: 'video/*',
    className: 'file-input',
    'aria-label': 'Select video file'
  });

  const uploadZone = el.div({
    className: 'upload-zone',
    id: 'uploadZone',
    role: 'button',
    tabindex: '0',
    'aria-label': 'Drop video file here or click to browse'
  }, [
    el.div({ className: 'upload-icon' }, ['\uD83D\uDCF9']),
    el.p({ className: 'upload-text' }, ['Drop your video here']),
    el.p({ className: 'upload-subtext' }, ['or click to browse']),
    el.p({ className: 'upload-formats' }, ['Supports MP4, WebM, MOV']),
    fileInput
  ]);

  // Click to open file picker
  uploadZone.addEventListener('click', () => fileInput.click());

  // Keyboard support
  uploadZone.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fileInput.click();
    }
  });

  // Drag and drop
  uploadZone.addEventListener('dragover', (e) => {
    e.preventDefault();
    uploadZone.classList.add('drag-over');
  });

  uploadZone.addEventListener('dragleave', () => {
    uploadZone.classList.remove('drag-over');
  });

  uploadZone.addEventListener('drop', (e) => {
    e.preventDefault();
    uploadZone.classList.remove('drag-over');
    const file = e.dataTransfer.files[0];
    if (file) {
      handlers.onFileSelect(file);
    }
  });

  // File input change
  fileInput.addEventListener('change', (e) => {
    const file = e.target.files[0];
    if (file) {
      handlers.onFileSelect(file);
    }
  });

  return uploadZone;
}

/**
 * Create the file info display component
 *
 * @returns {HTMLElement}
 */
export function createFileInfo() {
  return el.div({ className: 'file-info hidden', id: 'fileInfo' }, [
    el.div({ className: 'file-name', id: 'fileName' }),
    el.div({ className: 'file-details', id: 'fileDetails' }),
    el.div({ className: 'file-warning hidden', id: 'fileWarning' })
  ]);
}

/**
 * Update file info display
 *
 * @param {object} file - File info { name, size, duration, width, height, hasAudio }
 * @param {string|null} warning - Optional warning message
 */
export function updateFileInfo(file, warning = null) {
  const container = document.getElementById('fileInfo');
  const nameEl = document.getElementById('fileName');
  const detailsEl = document.getElementById('fileDetails');
  const warningEl = document.getElementById('fileWarning');

  if (!file) {
    hide(container);
    return;
  }

  show(container);
  setText(nameEl, truncateFilename(file.name, 40));

  const details = [
    formatFileSize(file.size),
    formatSecondsDecimal(file.duration),
    formatResolution(file.width, file.height),
    file.hasAudio ? 'Has audio' : 'No audio'
  ].join(' • ');
  setText(detailsEl, details);

  if (warning) {
    setText(warningEl, warning);
    show(warningEl);
  } else {
    hide(warningEl);
  }
}

/**
 * Create the configuration controls component
 *
 * @param {object} handlers - Event handlers
 * @param {function} handlers.onRepeatChange - Called when repeat count changes
 * @param {function} handlers.onCrossfadeChange - Called when crossfade duration changes
 * @returns {HTMLElement}
 */
export function createConfigControls(handlers) {
  // Repeat count slider
  const repeatSlider = el.input({
    type: 'range',
    id: 'repeatCount',
    min: String(MIN_REPEAT_COUNT),
    max: String(MAX_REPEAT_COUNT),
    value: '3',
    step: '1',
    className: 'slider',
    'aria-label': 'Repeat count'
  });

  // Crossfade duration slider
  const crossfadeSlider = el.input({
    type: 'range',
    id: 'crossfadeDuration',
    min: String(MIN_CROSSFADE_DURATION),
    max: String(MAX_CROSSFADE_DURATION),
    value: '1.0',
    step: '0.1',
    className: 'slider',
    'aria-label': 'Crossfade duration'
  });

  repeatSlider.addEventListener('input', (e) => {
    const value = parseInt(e.target.value, 10);
    setText(document.getElementById('repeatValue'), `${value}x`);
    handlers.onRepeatChange(value);
  });

  crossfadeSlider.addEventListener('input', (e) => {
    const value = parseFloat(e.target.value);
    setText(document.getElementById('crossfadeValue'), `${value.toFixed(1)}s`);
    handlers.onCrossfadeChange(value);
  });

  return el.div({ className: 'config-controls', id: 'configControls' }, [
    el.div({ className: 'control-group' }, [
      el.label({ className: 'control-label', for: 'repeatCount' }, ['Repeat count']),
      el.div({ className: 'slider-container' }, [
        repeatSlider,
        el.span({ className: 'slider-value', id: 'repeatValue' }, ['3x'])
      ])
    ]),
    el.div({ className: 'control-group' }, [
      el.label({ className: 'control-label', for: 'crossfadeDuration' }, ['Crossfade duration']),
      el.div({ className: 'slider-container' }, [
        crossfadeSlider,
        el.span({ className: 'slider-value', id: 'crossfadeValue' }, ['1.0s'])
      ])
    ]),
    el.div({ className: 'duration-estimate', id: 'durationEstimate' })
  ]);
}

/**
 * Update duration estimate display
 *
 * @param {number} clipDuration - Input clip duration
 * @param {number} repeatCount - Repeat count
 * @param {number} crossfadeDuration - Crossfade duration
 */
export function updateDurationEstimate(clipDuration, repeatCount, crossfadeDuration) {
  const el = document.getElementById('durationEstimate');
  if (!el) return;

  if (!clipDuration) {
    setText(el, '');
    return;
  }

  setText(el, formatDurationEstimate(clipDuration, repeatCount, crossfadeDuration));
}

/**
 * Create the process button component
 *
 * @param {object} handlers - Event handlers
 * @param {function} handlers.onProcess - Called when process button clicked
 * @returns {HTMLElement}
 */
export function createProcessButton(handlers) {
  const button = el.button({
    className: 'process-button',
    id: 'processButton',
    disabled: 'disabled',
    'aria-label': 'Process video'
  }, ['Process Video']);

  button.addEventListener('click', () => {
    if (!button.disabled) {
      handlers.onProcess();
    }
  });

  return button;
}

/**
 * Update process button state
 *
 * @param {object} options
 * @param {boolean} options.disabled - Whether button is disabled
 * @param {string} options.text - Button text
 * @param {boolean} options.loading - Whether to show loading state
 */
export function updateProcessButton({ disabled, text, loading }) {
  const button = document.getElementById('processButton');
  if (!button) return;

  button.disabled = disabled;
  setText(button, text);
  button.classList.toggle('loading', loading);
}

/**
 * Create the progress bar component
 *
 * @returns {HTMLElement}
 */
export function createProgressBar() {
  return el.div({ className: 'progress-container hidden', id: 'progressContainer' }, [
    el.div({ className: 'progress-bar' }, [
      el.div({ className: 'progress-fill', id: 'progressFill' })
    ]),
    el.div({ className: 'progress-text', id: 'progressText' }, ['0%']),
    el.div({ className: 'processing-banner', id: 'processingBanner' }, [
      el.p({ className: 'processing-banner-warning' }, [
        '⚠️ Keep this tab open — processing happens in your browser.'
      ]),
      el.p({ className: 'processing-banner-estimate', id: 'processingEstimate' }),
      el.p({ className: 'processing-banner-upsell' }, [
        'Want it faster? ',
        el.a({
          href: '#upgrade',
          className: 'processing-banner-link'
        }, ['Cloud rendering coming soon →'])
      ])
    ])
  ]);
}

/**
 * Update the processing banner time estimate
 *
 * @param {number} outputDuration - Expected output duration in seconds
 */
export function updateProcessingEstimate(outputDuration) {
  const estimateEl = document.getElementById('processingEstimate');
  if (!estimateEl || !outputDuration) return;

  // ffmpeg.wasm with crossfade filters runs at roughly 0.1x realtime
  const estimatedSeconds = Math.ceil(outputDuration / 0.1);

  let timeStr;
  if (estimatedSeconds < 60) {
    timeStr = `~${estimatedSeconds} seconds`;
  } else {
    const minutes = Math.round(estimatedSeconds / 60);
    timeStr = `~${minutes} minute${minutes > 1 ? 's' : ''}`;
  }

  setText(estimateEl, `Estimated time: ${timeStr}`);
}

/**
 * Update progress bar
 *
 * @param {number} percent - Progress percentage (0-100)
 */
export function updateProgress(percent) {
  const fill = document.getElementById('progressFill');
  const text = document.getElementById('progressText');

  if (fill) {
    fill.style.width = `${percent}%`;
  }
  if (text) {
    setText(text, formatPercent(percent));
  }
}

/**
 * Create the video preview component
 *
 * @returns {HTMLElement}
 */
export function createVideoPreview() {
  const video = el.video({
    id: 'previewVideo',
    controls: 'controls',
    className: 'preview-video',
    'aria-label': 'Processed video preview'
  });

  return el.div({ className: 'preview-container hidden', id: 'previewContainer' }, [
    el.h3({ className: 'section-title' }, ['Preview']),
    video
  ]);
}

/**
 * Update video preview
 *
 * @param {string} blobUrl - Blob URL for the video
 */
export function updatePreview(blobUrl) {
  const container = document.getElementById('previewContainer');
  const video = document.getElementById('previewVideo');

  if (!blobUrl) {
    hide(container);
    if (video) video.src = '';
    return;
  }

  show(container);
  if (video) {
    video.src = blobUrl;
  }
}

/**
 * Create the download button component
 *
 * @returns {HTMLElement}
 */
export function createDownloadSection() {
  const downloadLink = createElement('a', {
    id: 'downloadLink',
    className: 'download-button',
    download: 'output.mp4',
    'aria-label': 'Download processed video'
  }, ['Download']);

  return el.div({ className: 'download-section hidden', id: 'downloadSection' }, [
    downloadLink,
    el.div({ className: 'download-info', id: 'downloadInfo' })
  ]);
}

/**
 * Update download section
 *
 * @param {object} output - Output info { blobUrl, filename, fileSize }
 */
export function updateDownloadSection(output) {
  const container = document.getElementById('downloadSection');
  const link = document.getElementById('downloadLink');
  const info = document.getElementById('downloadInfo');

  if (!output) {
    hide(container);
    return;
  }

  show(container);
  if (link) {
    link.href = output.blobUrl;
    link.download = output.filename;
    setText(link, `Download ${output.filename}`);

    // Store current file size as data attribute so the click handler
    // always reads the latest value instead of closing over stale data
    link.dataset.outputSizeMb = String(Math.round(output.fileSize / 1024 / 1024));

    // Attach click listener once — reads current data attribute at click time
    if (!link.dataset.analyticsAttached) {
      link.dataset.analyticsAttached = 'true';
      link.addEventListener('click', () => {
        window.gtag?.('event', 'tool_download_complete', {
          output_size_mb: link.dataset.outputSizeMb
        });
      });
    }
  }
  if (info) {
    setText(info, `Size: ${formatFileSize(output.fileSize)}`);
  }
}

/**
 * Create the settings changed indicator component
 *
 * @returns {HTMLElement}
 */
export function createSettingsChangedIndicator() {
  return el.div({ className: 'settings-changed hidden', id: 'settingsChanged' }, [
    'Settings changed \u2014 re-process to update'
  ]);
}

/**
 * Create the error display component
 *
 * @param {object} handlers - Event handlers
 * @param {function} handlers.onRetry - Called when retry button clicked
 * @returns {HTMLElement}
 */
export function createErrorDisplay(handlers) {
  const retryButton = el.button({
    className: 'retry-button',
    id: 'retryButton'
  }, ['Try Again']);

  retryButton.addEventListener('click', () => {
    handlers.onRetry();
  });

  return el.div({ className: 'error-display hidden', id: 'errorDisplay' }, [
    el.div({ className: 'error-icon' }, ['\u26A0\uFE0F']),
    el.div({ className: 'error-message', id: 'errorMessage' }),
    retryButton
  ]);
}

/**
 * Show error message
 *
 * @param {string} message - Error message
 * @param {boolean} showRetry - Whether to show retry button
 */
export function showError(message, showRetry = true) {
  const container = document.getElementById('errorDisplay');
  const messageEl = document.getElementById('errorMessage');
  const retryBtn = document.getElementById('retryButton');

  if (container) show(container);
  if (messageEl) setText(messageEl, message);
  if (retryBtn) setVisible(retryBtn, showRetry);
}

/**
 * Hide error display
 */
export function hideError() {
  const container = document.getElementById('errorDisplay');
  if (container) hide(container);
}

/**
 * Create the upgrade prompt component
 *
 * @returns {HTMLElement}
 */
export function createUpgradePrompt() {
  return el.div({ className: 'upgrade-prompt hidden', id: 'upgradePrompt' }, [
    el.p({}, ['Need hours-long videos? Ken Burns effects? 4K?']),
    el.a({
      href: '#upgrade',
      className: 'upgrade-link',
      'aria-label': 'Learn about premium features'
    }, ['Discover Premium Features \u2192'])
  ]);
}

/**
 * Create the mobile warning component
 *
 * @returns {HTMLElement}
 */
export function createMobileWarning() {
  return el.div({ className: 'mobile-warning hidden', id: 'mobileWarning' }, [
    el.p({}, ['\u26A0\uFE0F This tool works best on desktop. Processing may be slow on mobile devices.'])
  ]);
}

/**
 * Create validation error display
 *
 * @returns {HTMLElement}
 */
export function createValidationError() {
  return el.div({ className: 'validation-error hidden', id: 'validationError' });
}

/**
 * Show validation error
 *
 * @param {string} message - Error message
 */
export function showValidationError(message) {
  const el = document.getElementById('validationError');
  if (el) {
    setText(el, message);
    show(el);
  }
}

/**
 * Hide validation error
 */
export function hideValidationError() {
  const el = document.getElementById('validationError');
  if (el) hide(el);
}
